import { FileText } from 'lucide-react'
import type { IntakeDetail } from '../api/types'

export interface DraftSummaryCardProps {
  intake: IntakeDetail
}

function formatCurrency(value: number): string {
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function pickShipTo(parsed: Record<string, unknown> | null): string | null {
  if (!parsed) return null
  const v = parsed['ship_to'] ?? parsed['shipping_address']
  if (typeof v === 'string') return v
  return null
}

/**
 * Compact summary above the line grid. Shows exactly the data that
 * gets sent to Odoo when the operator clicks "Send to Odoo". Mirrors
 * the upstream sale.order header — partner, PO#, dates, totals.
 */
export function DraftSummaryCard({ intake }: DraftSummaryCardProps) {
  // Computed total: sum(final_qty × final_unit_price) across lines.
  // Falls back to parsed values when finals are unset (operator hasn't
  // touched the line yet).
  let total = 0
  let hasAmounts = false
  for (const line of intake.lines) {
    const qty = Number(line.final_qty ?? line.parsed_qty ?? 0)
    const price = Number(line.final_unit_price ?? line.parsed_unit_price ?? 0)
    if (!Number.isNaN(qty) && !Number.isNaN(price) && qty > 0 && price > 0) {
      total += qty * price
      hasAmounts = true
    }
  }
  const totalDisplay = hasAmounts ? formatCurrency(total) : '—'
  const shipTo = pickShipTo(intake.parsed_payload)

  return (
    <section
      aria-label="Draft Sales Order Preview"
      className="rounded-sm border border-border-subtle bg-surface-sunk p-3"
    >
      <header className="mb-2 flex items-center gap-1.5">
        <FileText size={14} className="text-tertiary" />
        <h3 className="text-role-section-label m-0">
          Draft Sales Order — Preview
        </h3>
        <span
          className="ml-auto text-xs text-tertiary"
          title="This is exactly what gets sent to Odoo when you click Send to Odoo."
        >
          (sent to Odoo on confirm)
        </span>
      </header>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <div className="flex items-baseline gap-2">
          <dt className="text-tertiary text-xs uppercase tracking-wide">
            Customer
          </dt>
          <dd className="text-primary font-medium">
            {intake.customer_name || (
              <em className="text-tertiary font-normal">unset</em>
            )}
            {intake.customer_id ? (
              <span className="ml-1.5 text-xs text-tertiary">
                #{intake.customer_id}
              </span>
            ) : null}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-tertiary text-xs uppercase tracking-wide">
            PO #
          </dt>
          <dd className="text-primary">{intake.customer_po_number ?? '—'}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-tertiary text-xs uppercase tracking-wide">
            Requested delivery
          </dt>
          <dd className="text-primary">
            {intake.delivery_date_tbd ? 'TBD' : formatDate(intake.requested_delivery_date)}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-tertiary text-xs uppercase tracking-wide">
            Lines
          </dt>
          <dd className="text-primary">{intake.line_count}</dd>
        </div>
        {shipTo ? (
          <div className="col-span-2 flex items-baseline gap-2">
            <dt className="text-tertiary text-xs uppercase tracking-wide whitespace-nowrap">
              Ship-to
            </dt>
            <dd className="text-secondary text-xs leading-snug">{shipTo}</dd>
          </div>
        ) : null}
        <div className="col-span-2 flex items-baseline justify-end gap-2 border-t border-border-subtle pt-2 mt-1">
          <dt className="text-tertiary text-xs uppercase tracking-wide">
            Total
          </dt>
          <dd className="text-primary font-semibold text-base">
            {totalDisplay}
          </dd>
        </div>
      </dl>
    </section>
  )
}
